import type { ReactNode } from 'react';
import { styled } from 'goober';

import { breakpoints } from '@/theme/breakpoints';
import { mediaQuery } from '@/theme/setupTheme';
import { Stack } from '@/ui/line/line';

import { Pagination } from './pagination';

export interface CardGridPagination {
  onPageChange: (page: number) => void;
  page: number;
  pageSize: number;
  totalItems: number;
}

export interface CardGridResponsiveColumns {
  belowSm?: number;
  sm?: number;
}

interface CardGridProps {
  children: ReactNode;
  columns?: number;
  gap?: string;
  isLoading?: boolean;
  pagination?: CardGridPagination;
  responsiveColumns?: CardGridResponsiveColumns;
}

const DEFAULT_COLUMNS = 3;
const DEFAULT_GAP = '24px';
const SKELETON_ITEMS_COUNT = 6;

export const CardGrid = ({
  children,
  columns = DEFAULT_COLUMNS,
  gap = DEFAULT_GAP,
  isLoading = false,
  pagination,
  responsiveColumns,
}: CardGridProps): React.ReactElement => {
  return (
    <Stack gap={gap}>
      <GridContainer
        $columns={columns}
        $gap={gap}
        $responsiveColumns={responsiveColumns}
      >
        {isLoading ? (
          <>
            {Array.from({ length: SKELETON_ITEMS_COUNT }).map((_, index) => (
              <CardSkeleton key={index} />
            ))}
          </>
        ) : (
          children
        )}
      </GridContainer>
      {!isLoading && pagination && (
        <Pagination
          onPageChange={pagination.onPageChange}
          page={pagination.page}
          pageSize={pagination.pageSize}
          totalItems={pagination.totalItems}
        />
      )}
    </Stack>
  );
};

const GridContainer = styled('div')<{
  $columns: number;
  $gap: string;
  $responsiveColumns?: CardGridResponsiveColumns;
}>`
  display: grid;
  gap: ${({ $gap }) => $gap};
  grid-template-columns: repeat(${({ $columns }) => $columns}, minmax(0, 1fr));

  & > * {
    height: 100%;
    min-height: 0;
  }

  ${({ $responsiveColumns }) =>
    $responsiveColumns?.sm !== undefined
      ? `${mediaQuery('sm', 'max')} { grid-template-columns: repeat(${
          $responsiveColumns.sm
        }, minmax(0, 1fr)); }`
      : ''}

  ${({ $responsiveColumns }) =>
    $responsiveColumns?.belowSm !== undefined
      ? `@media (max-width: ${
          breakpoints.sm - 1
        }px) { grid-template-columns: repeat(${
          $responsiveColumns.belowSm
        }, minmax(0, 1fr)); }`
      : ''}
`;

const CardSkeleton = styled('div')`
  border-radius: 20px;
  height: 280px;
  background-color: ${({ theme }) => theme.colors.basics.white};
  box-shadow: ${({ theme }) => theme.shadow.widgets};
  ${({ theme }) => theme.animations.skeletonAnimation()};
`;
